/* Super Admin — Pilot KPI & Commercial Readiness dashboard (auto-calculated). */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Target, RefreshCw, Plus, ChevronDown, X, TrendingUp, Clock, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  "Ideal Target Achieved": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Minimum Target Achieved": "bg-teal-50 text-teal-700 border-teal-300",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-300",
  "Behind Target": "bg-orange-50 text-orange-700 border-orange-300",
  "Needs Attention": "bg-red-50 text-red-700 border-red-300",
  "Not Started": "bg-neutral-100 text-neutral-500 border-neutral-300",
  "Data Not Available / Tracking Required": "bg-neutral-100 text-neutral-500 border-dashed border-neutral-300",
};

const RANGES = [
  { k: "pilot", label: "Entire Pilot" }, { k: "today", label: "Today" },
  { k: "week", label: "This Week" }, { k: "lastweek", label: "Last Week" },
  { k: "month", label: "This Month" }, { k: "custom", label: "Custom" },
];

const iso = (d) => d.toISOString().slice(0, 10);
function rangeDates(k) {
  const now = new Date(); const day = now.getDay() || 7;
  if (k === "today") return [iso(now), iso(now)];
  if (k === "week") { const m = new Date(now); m.setDate(now.getDate() - day + 1); return [iso(m), iso(now)]; }
  if (k === "lastweek") { const m = new Date(now); m.setDate(now.getDate() - day - 6); const s = new Date(m); s.setDate(m.getDate() + 6); return [iso(m), iso(s)]; }
  if (k === "month") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
  return ["", ""];
}

export default function AdminKpi() {
  const [pilots, setPilots] = useState([]);
  const [pilotId, setPilotId] = useState("");
  const [range, setRange] = useState("pilot");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [showPilot, setShowPilot] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get("/kpi/pilots").then(({ data }) => {
      setPilots(data.pilots || []);
      if (data.pilots?.[0]) setPilotId(data.pilots[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!pilotId) return;
    const [s, e] = range === "custom" ? [custom.start, custom.end] : rangeDates(range);
    api.get("/kpi/dashboard", { params: { pilot_id: pilotId, start: s, end: e } })
      .then(({ data }) => setData(data)).catch(() => toast.error("Failed to load KPIs"));
  }, [pilotId, range, custom]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (card) => {
    if (!["teachers_registered", "teachers_activated", "teachers_2plus", "teachers_3plus"].includes(card.key)) return;
    const [s, e] = range === "custom" ? [custom.start, custom.end] : rangeDates(range);
    const { data: d } = await api.get("/kpi/drilldown", { params: { metric: card.key, pilot_id: pilotId, start: s, end: e } });
    setDrill({ card, ...d });
  };

  const syncDurations = async () => {
    setSyncing(true);
    try {
      const { data: d } = await api.post("/kpi/sync-meet-durations");
      toast.success(`Synced actual Meet durations for ${d.synced} class(es)`);
      load();
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const pilot = pilots.find((p) => p.id === pilotId);
  if (!data) return <div className="text-sm text-neutral-500">Loading KPI dashboard…</div>;
  const { cards, funnel, gate, trends, class_breakdown: cb, students, teaching, tech_issues: ti } = data;

  return (
    <div className="space-y-8" data-testid="kpi-dashboard">
      {/* Header + filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ pilot kpi &amp; commercial readiness</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><Target size={26} /> Pilot KPIs</h1>
          {pilot && <div className="text-xs text-neutral-500 mt-1">Partner: <strong>{pilot.partner}</strong> · Start: {pilot.start_date}{pilot.end_date ? ` · End: ${pilot.end_date}` : ""}</div>}
        </div>
        <select value={pilotId} onChange={(e) => setPilotId(e.target.value)} data-testid="kpi-pilot-select"
          className="border border-neutral-300 rounded-md px-3 py-2 text-sm bg-white">
          {pilots.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={() => setShowPilot(true)} data-testid="kpi-new-pilot" className="text-xs font-semibold border border-neutral-300 hover:border-black rounded-md px-3 py-2 inline-flex items-center gap-1"><Plus size={12} /> Pilot</button>
        <div className="flex rounded-md border border-neutral-300 overflow-hidden">
          {RANGES.map((r) => (
            <button key={r.k} onClick={() => setRange(r.k)} data-testid={`kpi-range-${r.k}`}
              className={`px-3 py-2 text-xs font-semibold ${range === r.k ? "bg-[#0A0C10] text-white" : "bg-white hover:bg-neutral-50"}`}>{r.label}</button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex gap-1.5">
            <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="kpi-custom-start" />
            <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs" data-testid="kpi-custom-end" />
          </div>
        )}
        <button onClick={syncDurations} disabled={syncing} data-testid="kpi-sync-durations"
          className="text-xs font-semibold border border-neutral-300 hover:border-black rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> Sync Meet durations
        </button>
      </div>

      {/* Launch gate */}
      <div className="bg-[#0A0C10] text-white rounded-2xl p-6" data-testid="kpi-launch-gate">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#22D3EE]">/ commercial launch gate</div>
            <div className="font-display font-bold text-2xl mt-1">{gate.ready ? "Ready for Commercial Launch" : "Not Yet Commercially Ready"}</div>
            <div className="text-sm text-white/60">{gate.summary}</div>
          </div>
          <div className="ml-auto text-5xl font-display font-bold">{gate.passed}<span className="text-white/40 text-2xl">/{gate.total}</span></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-5">
          {gate.items.map((g) => (
            <div key={g.name} className={`rounded-lg p-3 border ${!g.available ? "border-dashed border-white/20 bg-white/5" : g.passed ? "border-emerald-400/40 bg-emerald-400/10" : "border-amber-400/30 bg-white/5"}`} data-testid={`gate-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-xs font-semibold">{g.name}</div>
              <div className="text-[11px] text-white/60 mt-0.5">Target: {g.target}</div>
              <div className="text-sm mt-1">
                {!g.available ? <span className="text-white/50">Data Not Available / Tracking Required</span> : (
                  <>Actual: <strong>{g.actual ?? "—"}</strong> · {g.passed ? <span className="text-emerald-300 font-bold">Achieved</span> : <span className="text-amber-300">In Progress{g.gap ? ` · remaining ${g.gap}` : ""}</span>}</>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <button key={c.key} onClick={() => openDrill(c)} data-testid={`kpi-card-${c.key}`}
            className="text-left bg-white border border-neutral-200 rounded-xl p-4 hover:border-[#0A0C10] transition-colors">
            <div className="text-[11px] font-semibold text-neutral-500 leading-tight">{c.label}</div>
            <div className="font-display font-bold text-3xl mt-1.5">
              {c.current === null ? "—" : `${c.current}${c.unit}`}
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              Min: {c.minimum}{c.unit} · Ideal: {c.ideal}{c.unit}
              {c.achievement_pct !== null && c.current !== null && <> · <strong>{c.achievement_pct}%</strong> of min</>}
              {c.gap_to_minimum > 0 && <> · gap {c.gap_to_minimum}{c.unit}</>}
            </div>
            <div className={`inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded border ${STATUS_STYLE[c.status] || "bg-neutral-100 border-neutral-300"}`}>{c.status}</div>
            {c.note && <div className="text-[10px] text-neutral-400 mt-1.5 leading-snug">{c.note}</div>}
          </button>
        ))}
      </div>

      {/* Class breakdown + students + teaching hours */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="kpi-class-breakdown">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ class volume</div>
          {Object.entries({ Completed: cb.completed, Scheduled: cb.scheduled, "Live now": cb.live, Cancelled: cb.cancelled, Missed: cb.missed, Total: cb.total }).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">{k}</span><strong>{v}</strong></div>
          ))}
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="kpi-students">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ student usage</div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Unique students</span><strong>{students.unique}</strong></div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Total participations</span><strong>{students.participations}</strong></div>
          <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">Students with 2+ classes</span><strong>{students.multi_class}</strong></div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="kpi-teaching">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3 inline-flex items-center gap-1.5"><Clock size={12} /> / actual teaching (google meet)</div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Actual session hours</span><strong>{teaching.actual_hours}h</strong></div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Teacher presence hours</span><strong>{teaching.teacher_presence_hours}h</strong></div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Scheduled hours (completed)</span><strong>{teaching.scheduled_hours}h</strong></div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Avg actual class duration</span><strong>{teaching.avg_actual_min ? `${teaching.avg_actual_min}m` : "—"}</strong></div>
          <div className="flex justify-between text-sm py-1 border-b border-neutral-50"><span className="text-neutral-500">Scheduled vs actual delivery</span><strong>{teaching.delivery_pct ? `${teaching.delivery_pct}%` : "—"}</strong></div>
          <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">Classes below expected (&lt;70%)</span><strong>{teaching.below_expected}</strong></div>
          <div className="text-[10px] text-neutral-400 mt-2">Durations from Google Meet conference records ({teaching.synced_classes}/{teaching.completed_classes} completed classes synced). Rejoin intervals are summed, not spanned.</div>
        </div>
      </div>

      {/* Tech issue reports */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="kpi-tech-issues">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3 inline-flex items-center gap-1.5"><AlertTriangle size={12} /> / tech issue reports (live classes)</div>
        {!ti || ti.total_reports === 0 ? (
          <div className="text-sm text-neutral-500" data-testid="tech-issues-empty">
            No problems reported in this period. Teachers can report issues live via the "Report a problem" button in the classroom; every report lands here and is emailed to the admin.
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5">
            <div>
              <div className="flex gap-6">
                <div><div className="font-display font-bold text-3xl">{ti.total_reports}</div><div className="text-[11px] text-neutral-500">Total reports</div></div>
                <div><div className="font-display font-bold text-3xl">{ti.affected_classes}</div><div className="text-[11px] text-neutral-500">Classes affected</div></div>
                <div><div className="font-display font-bold text-3xl">{ti.issue_rate ?? "—"}%</div><div className="text-[11px] text-neutral-500">Issue rate</div></div>
              </div>
              <div className="mt-4 space-y-1.5">
                {ti.by_category.map((c) => {
                  const max = ti.by_category[0]?.count || 1;
                  return (
                    <div key={c.category} className="flex items-center gap-2" data-testid={`tech-cat-${c.category}`}>
                      <div className="w-32 text-[11px] font-semibold text-right">{c.label}</div>
                      <div className="flex-1 h-5 bg-neutral-50 rounded overflow-hidden">
                        <div className="h-full bg-amber-400 rounded flex items-center px-1.5" style={{ width: `${Math.max(8, (c.count / max) * 100)}%` }}>
                          <span className="text-[10px] font-bold text-[#0A0C10]">{c.count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] font-mono uppercase tracking-[0.12em] text-neutral-500">
                  <tr><th className="py-1.5 pr-3">When</th><th className="py-1.5 pr-3">Teacher</th><th className="py-1.5 pr-3">Class</th><th className="py-1.5 pr-3">Category</th><th className="py-1.5">Comment</th></tr>
                </thead>
                <tbody>
                  {ti.recent.map((r) => (
                    <tr key={r.id} className="border-t border-neutral-100" data-testid={`tech-row-${r.id}`}>
                      <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-1.5 pr-3 font-semibold">{r.reporter || "—"}</td>
                      <td className="py-1.5 pr-3">{r.class_title || "—"}</td>
                      <td className="py-1.5 pr-3"><span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">{r.label}</span></td>
                      <td className="py-1.5 text-neutral-500 max-w-[280px] truncate" title={r.comment}>{r.comment || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Funnel */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="kpi-funnel">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-4">/ conversion funnel</div>
        <div className="space-y-1.5">
          {funnel.map((f, i) => {
            const max = funnel.find((x) => x.count !== null)?.count || 1;
            const w = f.count === null ? 0 : Math.max(4, (f.count / Math.max(1, max)) * 100);
            return (
              <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-${i}`}>
                <div className="w-36 text-xs font-semibold text-right">{f.stage}</div>
                <div className="flex-1 h-7 bg-neutral-50 rounded overflow-hidden">
                  <div className="h-full bg-[#0A0C10] rounded flex items-center px-2" style={{ width: `${w}%` }}>
                    <span className="text-[11px] font-bold text-white">{f.count === null ? "n/a" : f.count}</span>
                  </div>
                </div>
                <div className="w-44 text-[11px] text-neutral-500">
                  {f.conv_from_prev !== null && <>↓ {f.conv_from_prev}% of previous · </>}
                  {f.conv_from_registered !== null && <>{f.conv_from_registered}% of registered</>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Weekly trends */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5 overflow-x-auto" data-testid="kpi-trends">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3 inline-flex items-center gap-1.5"><TrendingUp size={12} /> / weekly trends</div>
        {trends.length === 0 ? <div className="text-sm text-neutral-500">No activity in the selected period yet.</div> : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] font-mono uppercase tracking-[0.15em] text-neutral-500">
              <tr><th className="py-2 pr-4">Week</th><th className="py-2 pr-4">New Teachers</th><th className="py-2 pr-4">Teaching Teachers</th><th className="py-2 pr-4">First-Time</th><th className="py-2 pr-4">Classes</th><th className="py-2 pr-4">Students</th><th className="py-2 pr-4">Teaching Hours</th><th className="py-2">Paid</th></tr>
            </thead>
            <tbody>
              {trends.map((t) => (
                <tr key={t.week} className="border-t border-neutral-100">
                  <td className="py-1.5 pr-4 font-mono text-xs">{t.week}</td>
                  <td className="py-1.5 pr-4">{t.registrations}</td><td className="py-1.5 pr-4">{t.teachers}</td>
                  <td className="py-1.5 pr-4">{t.first_time}</td><td className="py-1.5 pr-4 font-bold">{t.classes}</td>
                  <td className="py-1.5 pr-4">{t.students}</td><td className="py-1.5 pr-4">{t.teaching_hours}h</td><td className="py-1.5">{t.paid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-neutral-400">{data.excluded_note} Click a teacher KPI card to see the underlying records.</div>

      {/* Drill-down modal */}
      {drill && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={() => setDrill(null)} data-testid="kpi-drilldown-modal">
          <div className="bg-white w-full max-w-5xl max-h-[88vh] overflow-y-auto rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">{drill.card.label} — {drill.count} teacher(s)</h2>
              <button onClick={() => setDrill(null)} className="p-2 hover:bg-neutral-100 rounded-md" data-testid="kpi-drill-close"><X size={16} /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] font-mono uppercase tracking-[0.12em] text-neutral-500">
                  <tr><th className="py-2 pr-3">Teacher</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Registered</th><th className="py-2 pr-3">First Class</th><th className="py-2 pr-3">Total</th><th className="py-2 pr-3">Completed</th><th className="py-2 pr-3">Teaching Min</th><th className="py-2 pr-3">Last Active</th><th className="py-2 pr-3">Board</th><th className="py-2 pr-3">AI</th><th className="py-2">Subscription</th></tr>
                </thead>
                <tbody>
                  {drill.rows.map((r) => (
                    <tr key={r.id} className="border-t border-neutral-100" data-testid={`drill-row-${r.id}`}>
                      <td className="py-1.5 pr-3 font-semibold">{r.name}</td><td className="py-1.5 pr-3 text-neutral-500">{r.email}</td>
                      <td className="py-1.5 pr-3">{r.registered}</td><td className="py-1.5 pr-3">{r.first_class || "—"}</td>
                      <td className="py-1.5 pr-3">{r.total_classes}</td><td className="py-1.5 pr-3 font-bold">{r.completed_classes}</td>
                      <td className="py-1.5 pr-3">{r.teaching_minutes || "—"}</td><td className="py-1.5 pr-3">{r.last_active || "—"}</td>
                      <td className="py-1.5 pr-3">{r.board_used ? "Yes" : "No"}</td><td className="py-1.5 pr-3">{r.ai_used ? "Yes" : "No"}</td>
                      <td className="py-1.5 capitalize">{r.subscription}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* New pilot modal */}
      {showPilot && <PilotModal onClose={() => setShowPilot(false)} onSaved={(p) => { setShowPilot(false); setPilots((ps) => [...ps, p]); setPilotId(p.id); }} />}
    </div>
  );
}

function PilotModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", partner: "", start_date: "", end_date: "", review_date: "" });
  const save = async () => {
    try {
      const { data } = await api.post("/kpi/pilots", f);
      toast.success("Pilot created");
      onSaved(data.pilot);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const inp = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]";
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="pilot-modal">
      <div className="bg-white w-full max-w-sm rounded-2xl p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display font-bold text-lg">New pilot</h2>
        <input className={inp} placeholder="Pilot name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pilot-name" />
        <input className={inp} placeholder="Partner / Institute" value={f.partner} onChange={(e) => setF({ ...f, partner: e.target.value })} data-testid="pilot-partner" />
        <label className="text-[11px] text-neutral-500">Start date<input type="date" className={inp} value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="pilot-start" /></label>
        <label className="text-[11px] text-neutral-500">End date (optional)<input type="date" className={inp} value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></label>
        <label className="text-[11px] text-neutral-500">Review date (optional)<input type="date" className={inp} value={f.review_date} onChange={(e) => setF({ ...f, review_date: e.target.value })} /></label>
        <button onClick={save} disabled={!f.name.trim()} data-testid="pilot-save" className="w-full py-2.5 rounded-md bg-[#0A0C10] text-white font-bold text-sm hover:bg-black disabled:opacity-50">Create pilot</button>
      </div>
    </div>
  );
}
